import {
  ORG_LIST,
  ALL_EQP_LIST,
} from "./constants";
import { isValidPnumFormat } from "./rowUtils";

function setField(result, key, level, message) {
  result.fieldStatus[key] = level;
  result.fieldMessages[key] = message;
  if (level === "error") result.hasError = true;
  if (level === "warning") result.hasWarning = true;
}

export function validateSingleRow(row, { duplicatePnums = new Set() } = {}) {
  const fieldStatus = {};
  const fieldMessages = {};
  const result = {
    fieldStatus,
    fieldMessages,
    hasError: false,
    hasWarning: false,
  };

  const sido = String(row.sido ?? "").trim();
  if (!sido || !ORG_LIST.includes(sido)) {
    setField(
      result,
      "sido",
      "error",
      sido ? `목록에 없는 시도: "${sido}"` : "시도를 선택하세요",
    );
  } else {
    fieldStatus.sido = "ok";
  }

  const eqp = String(row.eqp ?? "").trim();
  if (!eqp || !ALL_EQP_LIST.includes(eqp)) {
    setField(
      result,
      "eqp",
      "error",
      eqp ? `목록에 없는 차량종류: "${eqp}"` : "차량종류를 선택하세요",
    );
  } else {
    fieldStatus.eqp = "ok";
  }

  const callsgn = String(row.callsgn ?? "").trim();
  if (!callsgn) {
    setField(result, "callsgn", "error", "차량명칭은 필수입니다");
  } else {
    fieldStatus.callsgn = "ok";
  }

  const pnum = String(row.pnum ?? "").trim();
  if (!pnum) {
    setField(result, "pnum", "error", "선탑전화번호는 필수입니다");
  } else if (!isValidPnumFormat(pnum)) {
    setField(
      result,
      "pnum",
      "error",
      "전화번호 형식 오류 (000-0000-0000)",
    );
  } else if (duplicatePnums.has(pnum)) {
    setField(result, "pnum", "warning", "시트 내 중복 전화번호");
  } else {
    fieldStatus.pnum = "ok";
  }

  const rdvs = String(row.rdvs ?? "").trim();
  if (!rdvs) {
    setField(result, "rdvs", "warning", "자원집결지가 비어 있습니다");
  } else {
    fieldStatus.rdvs = "ok";
  }

  ["fireStation", "safetyCenter", "sumnum"].forEach((key) => {
    if (!fieldStatus[key] && String(row[key] ?? "").trim()) {
      fieldStatus[key] = "ok";
    }
  });

  let rowStatus = "normal";
  if (result.hasError) rowStatus = "error";
  else if (result.hasWarning) rowStatus = "warning";
  else if (Object.keys(fieldStatus).length === 0) rowStatus = "pending";

  return {
    ...row,
    fieldStatus,
    fieldMessages,
    rowStatus,
  };
}

export function validateRows(rows) {
  const pnumCounts = {};
  rows.forEach((row) => {
    const p = String(row.pnum ?? "").trim();
    if (p) pnumCounts[p] = (pnumCounts[p] || 0) + 1;
  });
  const duplicatePnums = new Set(
    Object.keys(pnumCounts).filter((p) => pnumCounts[p] > 1),
  );

  return rows.map((row) =>
    validateSingleRow(row, { duplicatePnums }),
  );
}

export function countValidation(rows) {
  let errors = 0;
  let warnings = 0;
  let normal = 0;
  rows.forEach((row) => {
    if (row.rowStatus === "error") errors += 1;
    else if (row.rowStatus === "warning") warnings += 1;
    else if (row.rowStatus === "normal") normal += 1;
  });
  return { errors, warnings, normal, total: rows.length };
}

export function summarizeIssues(rows) {
  const buckets = {
    phoneFormat: 0,
    requiredMissing: 0,
    duplicate: 0,
    other: 0,
  };
  rows.forEach((row) => {
    Object.entries(row.fieldMessages || {}).forEach(([, msg]) => {
      if (typeof msg !== "string" || !msg) return;
      if (msg.includes("전화번호")) buckets.phoneFormat += 1;
      else if (msg.includes("필수") || msg.includes("선택하세요"))
        buckets.requiredMissing += 1;
      else if (msg.includes("중복")) buckets.duplicate += 1;
      else buckets.other += 1;
    });
  });
  return buckets;
}
